import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CreditCard, ArrowUpCircle, Receipt, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { ReceiptSummary } from "@/components/ReceiptSummary";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

const STATUS = {
  active: ["Active", "bg-emerald-100 text-emerald-700"], authenticated: ["Starting soon", "bg-indigo-100 text-indigo-700"],
  pending: ["Payment failed · retrying", "bg-amber-100 text-amber-800"], halted: ["Renewal halted", "bg-rose-100 text-rose-700"],
  cancelling: ["Cancels at period end", "bg-amber-100 text-amber-800"], cancelled: ["Cancelled", "bg-slate-200 text-slate-700"],
  pending_verification: ["Awaiting webhook", "bg-amber-100 text-amber-800"], paused: ["Paused", "bg-slate-200 text-slate-700"],
  completed: ["Completed", "bg-slate-200 text-slate-700"], expired: ["Expired", "bg-slate-200 text-slate-700"],
};
const fmt = (iso) => iso ? new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—";

function openRazorpay(opts) {
  if (!window.Razorpay) { toast.error("Payment library not loaded. Refresh and try again."); return; }
  const rz = new window.Razorpay(opts);
  rz.on("payment.failed", (r) => toast.error(r?.error?.description || "Payment failed"));
  rz.open();
}

export function Billing({ t }) {
  const [b, setB] = useState(null);
  const [busy, setBusy] = useState("");
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [loadError, setLoadError] = useState("");
  const billingRequest = useRef(0);
  const load = async () => {
    const requestId = ++billingRequest.current;
    try {
      const { data } = await api.get("/subscription");
      if (requestId === billingRequest.current) { setB(data); setLoadError(""); }
    } catch {
      if (requestId === billingRequest.current) setLoadError("Billing status could not be loaded. Your subscription has not been changed.");
    }
  };
  useEffect(() => { load(); return () => { billingRequest.current += 1; }; }, []);

  const done = async (msg) => { if (msg) toast.success(msg); await refreshUser(); load(); };

  const cancel = async () => {
    setBusy("cancel");
    try { const { data } = await api.post("/subscription/cancel"); await done(data.message); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const updatePaymentMethod = async () => {
    setBusy("pm");
    try {
      const { data } = await api.post("/subscription/update-payment-method");
      if (data.mock) { const r = await api.post("/subscription/payment-method-updated"); await done(r.data.message); return; }
      openRazorpay({
        key: data.key_id, subscription_id: data.subscription_id, subscription_card_change: 1, recurring: 1,
        name: "NxT Plus", description: "Update payment method", prefill: { name: data.name, email: data.email }, theme: { color: "#4f46e5" },
        handler: async () => { try { const r = await api.post("/subscription/payment-method-updated"); await done(r.data.message); } catch (e) { toast.error(apiError(e)); } },
      });
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const upgrade = async () => {
    setBusy("upgrade");
    try {
      const { data } = await api.post("/subscription/upgrade");
      if (data.ok) { await done(data.message); return; }
      if (data.mode === "checkout_at_cycle_end") {
        toast(data.message);
        openRazorpay({
          key: data.key_id, subscription_id: data.subscription_id, name: "NxT Plus Annual", description: `₹${data.amount}/yr · starts ${fmt(data.starts_at)}`,
          prefill: { name: data.name, email: data.email }, theme: { color: "#4f46e5" },
          handler: async (res) => {
            try {
              const { data: v } = await api.post("/payments/verify", { razorpay_payment_id: res.razorpay_payment_id, razorpay_subscription_id: res.razorpay_subscription_id || data.subscription_id, razorpay_signature: res.razorpay_signature, plan: "yearly" });
              await done(v.activated ? "Annual plan authorised — it starts when your current month ends." : v.message);
            } catch (e) { toast.error(apiError(e)); }
          },
        });
      }
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  useEffect(() => {
    if (b && params.get("update_payment") === "1" && b.can_update_payment_method) {
      params.delete("update_payment"); setParams(params, { replace: true });
      updatePaymentMethod();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [b]);

  if (loadError) return <section data-testid="billing-load-error" className="rounded-3xl border p-6 text-sm"><p role="alert">{loadError}</p><button data-testid="billing-retry" onClick={load} className="mt-3 rounded-full border px-4 py-2 font-bold">Retry billing status</button></section>;
  if (!b) return <p data-testid="billing-loading" className="text-sm">Loading subscription…</p>;
  const isPlus = b.plan === "plus";
  const s = b.subscription;
  const [label, cls] = STATUS[s?.status] || ["", ""];
  const attention = s && ["pending", "halted"].includes(s.status);

  return (
    <div className="rounded-3xl border border-border bg-card p-5" data-testid="subscription-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{t("subscription")}</div>
          <div className="flex items-center gap-2">
            <div className="font-display text-lg font-bold" data-testid="billing-plan">{isPlus ? `NxT Plus${s?.plan ? ` · ${s.plan === "complimentary" ? "Complimentary" : s.plan === "yearly" ? "Annual" : "Monthly"}` : ""}` : t("free")}</div>
            {s && label && <span data-testid="billing-status" className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${cls}`}>{label}</span>}
          </div>
          {s?.status === "cancelling" && <p className="text-xs text-amber-600" data-testid="sub-cancelling-note">{t("cancelsAtEnd")}</p>}
          {isPlus && s?.expires_at && <p className="text-xs text-muted-foreground" data-testid="sub-access-until">{s.lifetime ? "Lifetime access" : `${t("accessUntil")} ${fmt(s.expires_at)}`}</p>}
        </div>
        {!isPlus && <button onClick={() => navigate("/paywall")} data-testid="profile-upgrade-btn" className="rounded-full bg-slate-900 px-4 py-2 text-sm font-bold text-white hover:bg-indigo-600">{t("upgrade")}</button>}
      </div>

      {isPlus && s && (
        <div className="mt-4 grid gap-2 sm:grid-cols-2">
          <div className="rounded-2xl bg-secondary p-3">
            <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Next charge</div>
            <div className="font-display text-base font-bold" data-testid="billing-next-charge">
              {s.lifetime ? "Lifetime access · nothing to pay" : !s.razorpay_subscription_id ? `Access until ${fmt(s.expires_at)} · no auto-renewal` : ["active", "authenticated", "pending"].includes(s.status) && !s.cancel_at_period_end ? `₹${s.amount?.toLocaleString("en-IN")} on ${fmt(s.next_charge_at || s.current_end || s.expires_at)}` : "No further charges"}
            </div>
          </div>
          <div className="rounded-2xl bg-secondary p-3">
            <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Payment method</div>
            <div className="font-display text-base font-bold capitalize" data-testid="billing-method">{s.payment_method || "Razorpay"}</div>
          </div>
        </div>
      )}

      {s?.billing_version === "monthly_v1" && <div data-testid="billing-lifecycle" className="mt-3 rounded-xl bg-secondary p-3 text-xs leading-relaxed"><p data-testid="billing-access-state">Premium access: {isPlus ? "ACTIVE through your paid period" : "Not active — core Career OS stays free"}</p><p data-testid="billing-subscription-id" className="break-all">Razorpay subscription: {s.razorpay_subscription_id || s.subscription_id}</p><p data-testid="billing-paid-through">Paid coverage ends: {fmt(s.expires_at)}</p>{s.payment_status === "awaiting_webhook" && <p data-testid="billing-awaiting-webhook">Payment captured. Waiting for the signed webhook; do not pay again.</p>}</div>}
      {attention && (
        <div className="mt-3 rounded-2xl border border-amber-300 bg-amber-50 p-3 text-sm" data-testid="billing-attention">
          <div className="font-bold text-amber-900">{s.status === "halted" ? "Your renewal stopped after repeated failures." : "Your last renewal failed — we're retrying."}</div>
          <div className="text-xs text-amber-800">Premium is available only through the already-paid period ending {fmt(s.expires_at)}. After that, it stays locked until a captured renewal and signed webhook confirm new coverage. Your free Career OS remains available.</div>
        </div>
      )}

      {isPlus && b.upgrade?.available && (
        <div className="mt-3 rounded-2xl border border-indigo-200 bg-indigo-50 p-4" data-testid="upgrade-annual-card">
          <div className="flex items-start gap-3">
            <ArrowUpCircle className="mt-0.5 h-5 w-5 shrink-0 text-indigo-600" />
            <div className="flex-1 text-sm">
              <div className="font-bold">Switch to Annual · save ₹{b.upgrade.saves_per_year.toLocaleString("en-IN")}/yr</div>
              <div className="mt-0.5 text-xs text-muted-foreground">
                Pay ≈ <b data-testid="upgrade-estimate">₹{b.upgrade.estimated_charge_now}</b> now for the {b.upgrade.remaining_days} days left in this cycle (₹{b.upgrade.unused_credit} unused credit applied), then ₹{b.upgrade.yearly_price.toLocaleString("en-IN")}/yr from {fmt(b.upgrade.next_charge_at)}. Exact amount is prorated by Razorpay.
              </div>
            </div>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button data-testid="upgrade-annual-btn" disabled={!!busy} className="rounded-full bg-indigo-600 px-4 py-2 text-xs font-bold text-white hover:bg-indigo-700 disabled:opacity-60">{busy === "upgrade" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Upgrade"}</button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Switch to NxT Plus Annual?</AlertDialogTitle>
                  <AlertDialogDescription>Razorpay will charge the prorated difference (≈₹{b.upgrade.estimated_charge_now}) on your saved payment method right now. From {fmt(b.upgrade.next_charge_at)} you'll be billed ₹{b.upgrade.yearly_price.toLocaleString("en-IN")} per year instead of ₹{b.upgrade.monthly_price} per month.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="upgrade-cancel-btn">Not now</AlertDialogCancel>
                  <AlertDialogAction data-testid="upgrade-confirm-btn" onClick={upgrade} className="bg-indigo-600 hover:bg-indigo-700">Confirm upgrade</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      )}

      {s && (
        <div className="mt-4 flex flex-wrap gap-2">
          {b.can_update_payment_method && (
            <button onClick={updatePaymentMethod} disabled={!!busy} data-testid="update-payment-method-btn" className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-bold disabled:opacity-60 ${attention ? "bg-amber-500 text-white hover:bg-amber-600" : "border border-border hover:bg-secondary"}`}>
              <CreditCard className="h-3.5 w-3.5" /> {busy === "pm" ? "Opening…" : "Update payment method"}
            </button>
          )}
          {b.can_cancel && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button data-testid="cancel-sub-btn" disabled={!!busy} className="rounded-full border border-border px-4 py-2 text-xs font-bold text-muted-foreground hover:text-destructive disabled:opacity-60">{t("cancel")}</button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Cancel NxT Plus?</AlertDialogTitle>
                  <AlertDialogDescription>{isPlus ? `You'll keep premium access until ${fmt(s.expires_at)}. ` : "Your premium access is already inactive. "}Your free roadmap and streak stay saved. This cancels future renewals only; it does not refund previous payments.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="cancel-sub-keep-btn">Keep Plus</AlertDialogCancel>
                  <AlertDialogAction data-testid="cancel-sub-confirm-btn" onClick={cancel} className="bg-destructive hover:bg-destructive/90">Cancel at period end</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      )}

      {!!b.payments?.some(p => p.payment_id && p.subscription_id) && <div data-testid="billing-receipt-history" className="mt-4 space-y-3"><h3 className="text-sm font-bold">Payment references and receipt emails</h3>{b.payments.filter(p => p.payment_id && p.subscription_id).map(p => <ReceiptSummary key={p.payment_id} testId={`billing-receipt-${p.payment_id}`} paymentId={p.payment_id} subscriptionId={p.subscription_id} receipt={p.receipt_email} paidThrough={p.period_end} />)}</div>}
      {b.payments?.length > 0 && (
        <details className="mt-4" data-testid="payment-history">
          <summary className="flex cursor-pointer items-center gap-2 text-sm font-semibold"><Receipt className="h-4 w-4 text-muted-foreground" /> Payment history · {b.payments.length}</summary>
          <div className="mt-2 space-y-1.5">
            {b.payments.map((p) => (
              <div key={p.payment_id || p.ts} className="flex items-center justify-between rounded-xl bg-secondary px-3 py-2 text-xs" data-testid={`payment-row-${p.payment_id}`}>
                <div>
                  <div className="font-bold">₹{p.amount?.toLocaleString("en-IN")} · {p.plan === "yearly" ? "Annual" : "Monthly"}{p.event === "subscription.upgrade" ? " upgrade" : ""}</div>
                  <div className="text-muted-foreground">{fmt(p.paid_at || p.ts)}{p.method ? ` · ${p.method}` : ""}{p.payment_id ? ` · ${p.payment_id}` : ""}</div>
                </div>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${p.status === "refunded" ? "bg-slate-200 text-slate-700" : p.status === "captured" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-800"}`}>{p.status}</span>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}
